import type { ReactNode } from "react";
import { kisalt, sayiMetni, seriRengi } from "./hesap";
import type { GrafikSerisi } from "./tipler";

export function GrafikDesenleri({
  kimlik,
  seriler,
}: {
  kimlik: string;
  seriler: readonly GrafikSerisi[];
}) {
  return (
    <defs>
      {seriler.map((seri, sira) => (
        <pattern
          key={`${seri.anahtar}-${sira}`}
          id={`${kimlik}-${sira}`}
          width="8"
          height="8"
          patternUnits="userSpaceOnUse"
          patternTransform={`rotate(${sira % 2 === 0 ? 45 : -45})`}
        >
          <rect width="8" height="8" fill={seriRengi(seri, sira)} />
          {sira % 3 === 0 ? (
            <path d="M 0 0 V 8" stroke="var(--color-notr-0)" strokeWidth="2" />
          ) : sira % 3 === 1 ? (
            <circle cx="4" cy="4" r="1.5" fill="var(--color-notr-0)" />
          ) : (
            <path d="M 0 0 H 8 M 0 0 V 8" stroke="var(--color-notr-0)" strokeWidth="1.5" />
          )}
        </pattern>
      ))}
    </defs>
  );
}

export function NoktaIsareti({
  x,
  y,
  sira,
  renk,
}: {
  x: number;
  y: number;
  sira: number;
  renk: string;
}) {
  const ortak = { fill: renk, stroke: "var(--color-notr-0)", strokeWidth: 1 };
  switch (sira % 4) {
    case 1:
      return <rect x={x - 4} y={y - 4} width="8" height="8" {...ortak} />;
    case 2:
      return <path d={`M ${x} ${y - 5} L ${x + 5} ${y + 4} L ${x - 5} ${y + 4} Z`} {...ortak} />;
    case 3:
      return (
        <path d={`M ${x} ${y - 5} L ${x + 5} ${y} L ${x} ${y + 5} L ${x - 5} ${y} Z`} {...ortak} />
      );
    default:
      return <circle cx={x} cy={y} r="4" {...ortak} />;
  }
}

export function GrafikAciklamasi({
  seriler,
  hedefVar,
  birim,
}: {
  seriler: readonly GrafikSerisi[];
  hedefVar: boolean;
  birim: string;
}) {
  const isaretler = ["daire", "kare", "üçgen", "baklava"];
  const desenler = ["çizgili", "noktalı", "ızgaralı"];
  return (
    <div
      className="text-notr-800 flex flex-wrap gap-x-3 gap-y-1 text-sm"
      style={{ overflowWrap: "anywhere" }}
    >
      {birim && <span>Birim: {birim === "%" ? "yüzde" : birim}</span>}
      {seriler.map((seri, sira) => (
        <span key={`${seri.anahtar}-${sira}`}>
          {sira + 1}. {seri.etiket} ({isaretler[sira % 4]} / {desenler[sira % 3]})
        </span>
      ))}
      {hedefVar && <span>Hedef: kesikli çizgi</span>}
    </div>
  );
}

export function GrafikEkseni({
  enAz,
  enCok,
  konum,
  children,
  birim = "",
}: {
  enAz: number;
  enCok: number;
  konum: (deger: number) => number;
  children: ReactNode;
  birim?: string;
}) {
  const degerler = enAz === enCok ? [enAz] : [enAz, enAz / 2 + enCok / 2, enCok];
  /*
    "31,5 net" 46 px'lik sol boşluğa sığmıyor ve "31,5 n…" diye kırpılıyordu.
    Biri sığmıyorsa birim HEPSİNDEN düşüyor (karışık "63 net" / "31,5" olmasın);
    birim grafiğin açıklama satırında yazılı, tam metin `<title>`da.
  */
  const birimsiz = degerler.some((deger) => sayiMetni(deger, birim).length > 7);
  return (
    <g fill="var(--color-notr-800)" fontSize="12">
      {degerler.map((deger, sira) => (
        <g key={sira}>
          <line
            x1="52"
            x2="348"
            y1={konum(deger)}
            y2={konum(deger)}
            stroke="var(--color-notr-200)"
          />
          <text x="46" y={konum(deger) + 4} textAnchor="end">
            <title>{sayiMetni(deger, birim)}</title>
            {kisalt(sayiMetni(deger, birimsiz ? "" : birim), 7)}
          </text>
        </g>
      ))}
      {children}
    </g>
  );
}

export function HedefCizgisi({
  hedefler,
  x,
  y,
  sol = 52,
  sag = 348,
  birim = "",
}: {
  hedefler: readonly (number | null)[];
  x: (sira: number) => number;
  y: (deger: number) => number;
  sol?: number;
  sag?: number;
  birim?: string;
}) {
  const ilk = hedefler[0];
  if (ilk != null && hedefler.every((hedef) => hedef === ilk)) {
    return (
      <line
        data-testid="hedef-cizgisi"
        x1={sol}
        x2={sag}
        y1={y(ilk)}
        y2={y(ilk)}
        stroke="var(--color-vurgu-600)"
        strokeWidth="2"
        strokeDasharray="6 4"
      >
        <title>Hedef: {sayiMetni(ilk, birim)}</title>
      </line>
    );
  }
  let devam = false;
  const yol = hedefler
    .map((hedef, sira) => {
      if (hedef === null) {
        devam = false;
        return "";
      }
      const parca = `${devam ? "L" : "M"} ${x(sira)} ${y(hedef)}`;
      devam = true;
      return parca;
    })
    .join(" ");
  if (!yol.trim()) return null;
  return (
    <g>
      <path
        data-testid="hedef-cizgisi"
        d={yol}
        fill="none"
        stroke="var(--color-vurgu-600)"
        strokeWidth="2"
        strokeDasharray="6 4"
      />
      {hedefler.map((hedef, sira) =>
        hedef === null ? null : (
          <circle key={sira} cx={x(sira)} cy={y(hedef)} r="3" fill="var(--color-vurgu-600)">
            <title>Hedef: {sayiMetni(hedef, birim)}</title>
          </circle>
        ),
      )}
    </g>
  );
}
